import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileWarning, Layers, Scale } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import type { EntitlementGap, PortfolioClaimSummary } from "@hp/types";
import { fetchPortfolioAgreementDetail } from "../api/portfolioDetail";
import { ClaimValueTableCells } from "../components/claims/ClaimValueTableCells";
import { Badge, Card, CardBody, CardHeader, CardTitle, StatusPill } from "@hp/ui";
import { ErrorBlock, LoadingBlock } from "../components/QueryStatus";
import { formatCount, formatDate, formatForum, formatINR, formatINRRange, formatIssueTag } from "../lib/format";
import { claimValueParts } from "../lib/claimValue";
import { OVERVIEW_ONLY } from "../lib/demoMode";

/** Same ordering as claimCandidateGroups' representative pick: furthest-along status first, then confidence. */
const CLAIM_STATUS_RANK: Record<string, number> = {
  rejected: -1,
  identified: 0,
  deferred: 1,
  validated: 2,
  filed: 3,
  allowed: 4,
  recovered: 5,
};

function pickRepresentativeClaim(claims: PortfolioClaimSummary[]): PortfolioClaimSummary | null {
  let best: PortfolioClaimSummary | null = null;
  for (const claim of claims) {
    if (!best) {
      best = claim;
      continue;
    }
    const rankDiff = (CLAIM_STATUS_RANK[claim.status] ?? 0) - (CLAIM_STATUS_RANK[best.status] ?? 0);
    if (rankDiff > 0 || (rankDiff === 0 && claim.confidence_score > best.confidence_score)) best = claim;
  }
  return best;
}

const STATUS_BADGES: Record<string, { label: string; tone: "warning" | "info" | "success" | "neutral" }> = {
  needs_ocr: { label: "Needs OCR", tone: "warning" },
  extracted: { label: "Clauses extracted", tone: "info" },
  ledger_ready: { label: "Ledger ready", tone: "success" },
  placeholder: { label: "Placeholder", tone: "neutral" },
};

export function PortfolioDetailPage() {
  const { agreementId = "" } = useParams<{ agreementId: string }>();
  const detailQuery = useQuery({
    queryKey: ["portfolio-detail", agreementId],
    queryFn: () => fetchPortfolioAgreementDetail(agreementId),
    enabled: Boolean(agreementId),
  });

  if (detailQuery.isPending) return <LoadingBlock label="Loading contract detail" surface="canvas" />;
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorBlock message="Could not load this contract's portfolio detail." surface="canvas" />;
  }

  const detail = detailQuery.data;
  const agreement = detail.agreement;
  const hasOpenClaims = agreement.flagged_claim_count > 0;

  // Overview-only demo: all FY tranches of one issue are ONE claim, so the "Issue open" tile counts
  // distinct issues and links to that claim's detail page (representative = furthest-along tranche).
  const openIssueCount = new Set(detail.open_claims.map((claim) => claim.issue_tag)).size;
  const representativeClaim = pickRepresentativeClaim(detail.open_claims);
  // The API only totals the high end across a contract's open claims; the low end has to be summed
  // client-side from each claim's own value_low / breakdown, the same split ClaimValueTableCells uses.
  const openClaimsTotals = detail.open_claims.reduce(
    (sum, claim) => {
      const parts = claimValueParts(claim.value_low, claim.value_high, claim.value_breakdown);
      return {
        principalLow: sum.principalLow + parts.principalLow,
        carryingLow: sum.carryingLow + parts.carryingLow,
        totalLow: sum.totalLow + claim.value_low,
      };
    },
    { principalLow: 0, carryingLow: 0, totalLow: 0 },
  );
  const headingMeta = OVERVIEW_ONLY
    ? [
        formatForum(agreement.forum),
        agreement.effective_date ? `Effective ${formatDate(agreement.effective_date)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  return (
    <div className="page-stack">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Back to overview
      </Link>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <h1 className="text-xl font-semibold text-zinc-900 sm:text-2xl">{agreement.name}</h1>
          <Badge tone={STATUS_BADGES[detail.processing_status]?.tone ?? "neutral"}>
            {STATUS_BADGES[detail.processing_status]?.label ?? detail.processing_status}
          </Badge>
        </div>
        <p className="text-sm text-zinc-600">{agreement.counterparty}</p>
        {OVERVIEW_ONLY ? (
          <p className="text-sm text-zinc-500">{headingMeta}</p>
        ) : (
          <p className="text-sm text-zinc-500">
            {formatForum(agreement.forum)} · {formatIssueTag(agreement.agreement_type)} · Effective {formatDate(agreement.effective_date)}
            {agreement.contracted_share != null ? ` · ${(agreement.contracted_share * 100).toFixed(0)}% contracted share` : null}
          </p>
        )}
        {detail.source_pdf ? (
          <p className="text-xs text-zinc-500">Source PDF: {detail.source_pdf}</p>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Flagged claims</p>
            <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(agreement.flagged_claim_count)}</p>
            <p className="text-sm text-zinc-600">{hasOpenClaims ? "open candidates" : "—"}</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Principal (RoE)</p>
            <p className="mt-1 text-2xl font-semibold font-mono tabular-nums text-zinc-900">
              {hasOpenClaims
                ? formatINRRange(openClaimsTotals.principalLow, detail.open_claims_principal_high_total)
                : "—"}
            </p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Late payment surcharge</p>
            <p className="mt-1 text-2xl font-semibold font-mono tabular-nums text-zinc-900">
              {hasOpenClaims && detail.open_claims_carrying_cost_high_total > 0
                ? formatINRRange(openClaimsTotals.carryingLow, detail.open_claims_carrying_cost_high_total)
                : "—"}
            </p>
            <p className="text-sm text-zinc-600">LPS</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Total recoverable</p>
            <p className="mt-1 text-2xl font-semibold font-mono tabular-nums text-brand-900">
              {hasOpenClaims
                ? formatINRRange(openClaimsTotals.totalLow, detail.open_claims_value_high_total)
                : "—"}
            </p>
            <p className="text-sm text-zinc-600">principal + LPS</p>
          </CardBody>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Ledger</p>
            <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(detail.line_item_count)}</p>
            <p className="text-sm text-zinc-600">tariff line items · {formatCount(detail.gap_count)} gaps</p>
          </CardBody>
        </Card>
        {OVERVIEW_ONLY ? (
          <Card>
            {representativeClaim ? (
              <Link
                to={`/reports/potential/${representativeClaim.id}`}
                state={{ backTo: `/portfolio/${agreementId}`, backLabel: agreement.name }}
                className="group block rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600"
                aria-label={`Issue open: ${formatCount(openIssueCount)}. View claim`}
              >
                <CardBody className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-zinc-500">Issue open</p>
                    <span className="text-xs font-medium text-brand-700 group-hover:underline">View claim ↗</span>
                  </div>
                  <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(openIssueCount)}</p>
                  <p className="text-sm text-zinc-600">
                    {formatINRRange(openClaimsTotals.principalLow, detail.open_claims_principal_high_total)} principal
                  </p>
                </CardBody>
              </Link>
            ) : (
              <CardBody className="p-4">
                <p className="text-xs uppercase tracking-wide text-zinc-500">Issue open</p>
                <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(openIssueCount)}</p>
                <p className="text-sm text-zinc-600">—</p>
              </CardBody>
            )}
          </Card>
        ) : (
          <Card>
            <CardBody className="p-4">
              <p className="text-xs uppercase tracking-wide text-zinc-500">Unclaimed gaps</p>
              <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(detail.unclaimed_gap_count)}</p>
              <p className="text-sm text-zinc-600">
                {detail.unclaimed_gap_count > 0 ? formatINR(detail.unclaimed_gap_value_high) : "—"}
              </p>
            </CardBody>
          </Card>
        )}
        <Card>
          <CardBody className="p-4">
            <p className="text-xs uppercase tracking-wide text-zinc-500">Clauses</p>
            <p className="mt-1 text-2xl font-semibold text-zinc-900">{formatCount(detail.clause_count)}</p>
            <p className="text-sm text-zinc-600">{formatCount(detail.tax_position_count)} tax positions</p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileWarning className="h-4 w-4 text-amber-500" aria-hidden />
            <CardTitle>Potential claims from this contract</CardTitle>
          </div>
        </CardHeader>
        <CardBody className="space-y-3 text-sm text-zinc-700">
          <p>{detail.potential_claims_note}</p>
          {detail.next_steps.length > 0 ? (
            <ul className="list-disc space-y-1 pl-5 text-zinc-600">
              {detail.next_steps.map((step: string) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>

      {detail.open_claims.length > 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Scale className="h-4 w-4 text-zinc-400" aria-hidden />
              <CardTitle>Open claim candidates</CardTitle>
            </div>
            <p className="mt-1 text-xs text-zinc-500">
              {OVERVIEW_ONLY
                ? "Financial-year tranches of this claim, with the principal, late payment surcharge and total for each year."
                : "Click a row for full value breakdown, precedent chain, and matched clause."}
            </p>
          </CardHeader>
          <CardBody className="p-0">
            <div className="table-scroll">
            <table className="table-data text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="min-w-[8rem] font-medium">Issue</th>
                  <th className="min-w-[4rem] font-medium">FY</th>
                  <th className="col-hide-md min-w-[6rem] font-medium">Finding</th>
                  <th className="min-w-[5rem] font-medium">Principal</th>
                  <th className="col-hide-lg min-w-[4rem] font-medium">LPS</th>
                  <th className="min-w-[5rem] font-medium">Total</th>
                  <th className="min-w-[5rem] font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {detail.open_claims.map((claim: PortfolioClaimSummary) => (
                  <tr key={claim.id} className="border-b border-zinc-100 last:border-0">
                    <td>
                      <Link
                        to={`/reports/potential/${claim.id}`}
                        state={{ backTo: `/portfolio/${agreementId}`, backLabel: agreement.name }}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {formatIssueTag(claim.issue_tag)}
                      </Link>
                    </td>
                    <td className="text-zinc-600">{claim.financial_year ?? "—"}</td>
                    <td className="col-hide-md text-zinc-600">{claim.finding_case_number ?? "—"}</td>
                    <ClaimValueTableCells
                      valueLow={claim.value_low}
                      valueHigh={claim.value_high}
                      breakdown={claim.value_breakdown}
                    />
                    <td>
                      {OVERVIEW_ONLY ? (
                        <StatusPill kind="claim_candidate" status={claim.status} />
                      ) : (
                        <Link
                          to={`/reports/potential/${claim.id}`}
                          state={{ backTo: `/portfolio/${agreementId}`, backLabel: agreement.name }}
                        >
                          <StatusPill kind="claim_candidate" status={claim.status} />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!OVERVIEW_ONLY && detail.gaps.length > 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-zinc-400" aria-hidden />
              <CardTitle>Entitlement gaps</CardTitle>
            </div>
          </CardHeader>
          <CardBody className="p-0">
            <div className="table-scroll">
              <table className="table-data text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="min-w-[4rem] font-medium">FY</th>
                    <th className="min-w-[6rem] font-medium">Head</th>
                    <th className="min-w-[6rem] font-medium">Gap (contracted)</th>
                    <th className="min-w-[5rem] font-medium">State</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.gaps.map((gap: EntitlementGap) => (
                    <tr key={gap.id} className="border-b border-zinc-100 last:border-0">
                      <td>{gap.financial_year}</td>
                      <td>{formatIssueTag(gap.head)}</td>
                      <td className="font-mono tabular-nums">
                        {formatINR(gap.gap_contracted ?? gap.gap_project_level)}
                      </td>
                      <td className="text-zinc-600">{gap.claim_state}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
